import { Link } from 'react-router-dom';
import { ArrowUpRight, Plus } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { toast } from 'sonner';
import { useCart } from '@/context/CartContext';
import { formatPrice } from '@/data/products';
import { useProducts } from '@/lib/shopify';

const Frame = ({ collection, product, className }) => {
  const { add, setOpen } = useCart();
  if (!product) return null;
  return (
  <Reveal className={`w-[82vw] shrink-0 snap-start md:w-auto ${className}`}>
    <article className="group relative h-full overflow-hidden bg-[#E6E6E9]" data-testid={`shop-collection-card-${collection.handle}`}>
      <Link to={`/shop?collection=${collection.handle}`} data-testid={`shop-collection-link-${collection.handle}`} className="block h-full">
        <img src={product.image} alt={product.name} loading="lazy" className="h-full w-full object-contain transition-transform duration-700 group-hover:scale-[1.035]" />
        <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/70 via-black/10 to-transparent px-5 pb-5 pt-16 text-white">
          <div>
            <p className="font-mono2 text-[9px] font-bold uppercase tracking-[0.22em]">{collection.productCount} pieces</p>
            <h3 className="mt-1 font-syne text-xl font-bold uppercase">{collection.title}</h3>
          </div>
          <ArrowUpRight size={22} />
        </div>
      </Link>
      <button
        type="button"
        data-testid={`shop-collection-quick-add-${collection.handle}`}
        onClick={() => { add(product); toast.success(`${product.name} added to cart`); setOpen(true); }}
        className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center border border-white/70 bg-white text-[#1E1E1E] transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-[#C7D1C3]"
        aria-label={`Quick add ${product.name}`}
      >
        <Plus size={17} />
      </button>
    </article>
  </Reveal>
  );
};

export const EditorialSpread = () => {
  const { products, collections, loading } = useProducts();
  const showcase = collections.map((collection) => ({
    collection,
    product: products.find((product) => product.collections?.some((item) => item.handle === collection.handle)),
  })).filter((entry) => entry.product);
  return (
  <section className="bg-[#FAFAFA]/80 px-5 py-24 text-[#1E1E1E] backdrop-blur-[2px] sm:px-8 lg:px-12 lg:py-36" data-testid="editorial-spread">
    <div className="mx-auto max-w-[1500px]">
      <div className="mb-14 grid gap-6 lg:grid-cols-12">
        <Reveal className="lg:col-span-8">
          <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.28em] text-black/55">From the current Shopify collection</p>
          <h2 className="mt-4 max-w-4xl font-syne text-4xl font-extrabold uppercase leading-[0.9] sm:text-5xl lg:text-6xl">
            Shop the pieces.<br /><span className="steel-text">Wear the point of view.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="flex items-end lg:col-span-3 lg:col-start-10">
          <p className="max-w-sm text-sm leading-relaxed text-black/65">
            Everything here is pulled from your live Shopify inventory. Open a collection, choose the right color and size, then add it to bag.
          </p>
        </Reveal>
      </div>

      <div className="-mx-5 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-3 sm:-mx-8 sm:px-8 md:mx-0 md:grid md:auto-rows-[390px] md:overflow-visible md:px-0 lg:auto-rows-[460px]" data-testid="shop-collection-showcase">
        {showcase.map(({ collection, product }, index) => <Frame key={collection.handle} collection={collection} product={product} className={index % 3 === 0 ? 'md:col-span-6' : 'md:col-span-3'} />)}
        {!loading && showcase.length === 0 && <Link to="/shop" data-testid="editorial-shop-link" className="flex items-end justify-between border border-black p-6 font-syne text-2xl font-extrabold uppercase sm:text-3xl">Shop current pieces <ArrowUpRight size={28} /></Link>}
      </div>
    </div>
  </section>
  );
};